import { apiFetch, getApiBaseUrl, getAuthToken } from "@/lib/api";

export type Proposal = {
  id: string;
  client_id?: number | null;
  partner_id?: number | null;
  owner_id?: string | null;
  created_by?: string | null;
  title?: string | null;
  main_destination?: string | null;
  vat_exempt?: boolean | null;
  status?: string | null;
  priority?: string | null;
  proposal_category?: "individual" | "family" | "business" | null;
  sector?: "tourism" | "hotels" | "transport" | "events" | "esg" | "sports" | null;
  language?: string | null;
  currency?: string | null;
  pricing_adjustment_type?: "none" | "commission" | "discount" | null;
  pricing_adjustment_percentage?: number | null;
  price_tier_override?: "operator" | "agency" | "final" | null;
  public_pricing_view?: "adjusted" | "base" | null;
  context_observations?: string | null;
  internal_reference?: string | null;
  pax_adults?: number | null;
  pax_children?: number | null;
  pax_infants?: number | null;
  passenger_mode_detailed?: boolean | null;
  terms_and_conditions?: string | null;
  last_step?: number | null;
  sent_at?: string | null;
  expiry_date?: string | null;
  confirmed_at?: string | null;
  confirmed_by_client_ip?: string | null;
  first_service_start_date?: string | null;
  last_service_end_date?: string | null;
  edit_unlocked?: boolean | null;
  edit_unlocked_by?: string | null;
  edit_unlocked_at?: string | null;
  edit_unlock_reason?: string | null;
  passengers?: any[];
  trip_legs?: any[];
  airport_services?: any[];
  transfers?: any[];
  flights?: any[];
  accommodations?: any[];
  tours?: any[];
  attractions?: any[];
  additional_requests?: any[];
  insurance?: any[];
  handling_fee?: any[];
  total_value?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  cancellation_reason?: string | null;
  cancellation_requested_by?: string | null;
  cancellation_review_manager_id?: string | null;
  cancellation_requested_at?: string | null;
  cancellation_decision_type?: "controllable" | "uncontrollable" | null;
  cancellation_decided_by?: string | null;
  cancellation_decided_at?: string | null;
};

export type ProposalListItem = {
  id: string;
  internal_reference?: string | null;
  title?: string | null;
  main_destination?: string | null;
  status?: string | null;
  priority?: string | null;
  owner_id?: string | null;
  clients?: any | null;
  total_value?: number | null;
  created_at?: string | null;
};

export type ProposalListResponse = {
  items: ProposalListItem[];
  total: number;
};

export type ProposalStatusHistoryEntry = {
  status?: string | null;
  entered_at?: string | null;
};

export const fetchProposals = async (params: {
  page: number;
  size: number;
  search?: string;
  sortField?: string | null;
  sortOrder?: string | null;
  status?: string | null;
  priority?: string | null;
  ownerId?: string | null;
}): Promise<ProposalListResponse> => {
  const query = new URLSearchParams();
  query.set("page", String(params.page));
  query.set("size", String(params.size));
  if (params.search) query.set("search", params.search);
  if (params.sortField) query.set("sortField", params.sortField);
  if (params.sortOrder) query.set("sortOrder", params.sortOrder);
  if (params.status) query.set("status", params.status);
  if (params.priority) query.set("priority", params.priority);
  if (params.ownerId) query.set("owner_id", params.ownerId);
  const payload = await apiFetch<ProposalListResponse | null>(`/proposals?${query.toString()}`);
  return payload ?? { items: [], total: 0 };
};

export const getProposal = async (id: string): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/${id}`);
};

export interface ProposalStatusInfo {
  id: string;
  status: string | null;
  updated_at: string | null;
}

export const getProposalStatus = async (id: string): Promise<ProposalStatusInfo> => {
  return apiFetch<ProposalStatusInfo>(`/proposals/${id}/status`);
};

export const getProposalStatusHistory = async (id: string): Promise<ProposalStatusHistoryEntry[]> => {
  const response = await apiFetch<ProposalStatusHistoryEntry[] | null>(`/proposals/${id}/status-history`);
  return response ?? [];
};

export const getPublicProposal = async (id: string): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/public/${id}`);
};

export const getPublicProposalByShareToken = async (token: string): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/public/share/${token}`);
};

export type ProposalPricingView = "adjusted" | "base";

export type ProposalPublicLink = {
  token: string;
  pricing_view: ProposalPricingView;
};

export const getOrCreateProposalPublicLink = async (
  id: string,
  pricingView: ProposalPricingView
): Promise<ProposalPublicLink> => {
  return apiFetch<ProposalPublicLink>(`/proposals/${id}/public-links`, {
    method: "POST",
    body: JSON.stringify({ pricing_view: pricingView }),
  });
};

export const createProposal = async (
  data: Partial<Proposal>,
  options?: { importMode?: boolean }
): Promise<Proposal> => {
  const query = options?.importMode ? "?import_mode=true" : "";
  return apiFetch<Proposal>(`/proposals${query}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export interface ProposalUpdateResult {
  id: string;
  status: string | null;
  last_step: number | null;
  total_value: number | null;
  first_service_start_date: string | null;
  last_service_end_date: string | null;
  updated_at: string | null;
}

export const updateProposal = async (id: string, data: Partial<Proposal>): Promise<ProposalUpdateResult> => {
  return apiFetch<ProposalUpdateResult>(`/proposals/${id}` , {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const updateProposalStatus = async (
  id: string,
  data: { status: string; sent_at?: string | null; expiry_date?: string | null }
): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/${id}/status`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const updateProposalOwner = async (id: string, ownerId: string): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/${id}/owner`, {
    method: "PUT",
    body: JSON.stringify({ owner_id: ownerId }),
  });
};

// CEO-only. Reopens a proposal that its status froze (e.g. once the invoice PDF landed) without
// changing that status. While unlocked only the CEO can save; it stays open until locked again.
export const unlockProposalForEditing = async (id: string, reason?: string): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/${id}/edit-lock/unlock`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
};

export const lockProposalForEditing = async (id: string): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/${id}/edit-lock/lock`, { method: "POST" });
};

export const updateProposalSelection = async (
  id: string,
  payload: { type: string; item_id: number; selected: boolean }
): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/${id}/selections`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

export const confirmProposal = async (id: string): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/${id}/confirm`, { method: "POST" });
};

export const deleteProposal = async (id: string): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}`, { method: "DELETE" });
};

export const deleteAllProposals = async (): Promise<void> => {
  await apiFetch<void>("/proposals", { method: "DELETE" });
};

export type ProposalInvoice = {
  id: string;
  proposal_id: string;
  file_name: string;
  file_size: number | null;
  uploaded_by: string | null;
  uploader_name?: string | null;
  uploaded_at: string;
  download_url: string | null;
  view_url: string | null;
};

export type ProposalReviewLog = {
  id: string;
  action: string;
  reason: string | null;
  created_at: string;
  performed_by: string | null;
  performer_name?: string | null;
};

export type ProposalRevisionActor = {
  actor_id?: string | null;
  actor_name?: string | null;
  actor_role?: string | null;
};

export type ProposalRevisionChange = {
  field_path: string;
  before_value: any;
  after_value: any;
};

export type ProposalRevision = {
  id: string;
  proposal_id: string;
  version: number;
  action: string;
  summary?: string | null;
  actor?: ProposalRevisionActor | null;
  changes?: ProposalRevisionChange[];
  created_at: string;
};

export type ProposalRevisionListResponse = {
  items: ProposalRevision[];
  total: number;
};

export const getProposalInvoice = async (id: string): Promise<ProposalInvoice | null> => {
  try {
    const response = await apiFetch<ProposalInvoice | string | null>(`/proposals/${id}/invoice`);
    if (!response || typeof response === "string") return null;
    return response;
  } catch (error: any) {
    if (error?.status === 204) return null;
    if (error?.status === 404) return null;
    throw error;
  }
};

export const listProposalInvoices = async (id: string): Promise<ProposalInvoice[]> => {
  const response = await apiFetch<ProposalInvoice[] | null>(`/proposals/${id}/invoices`);
  return response ?? [];
};

export const deleteProposalInvoice = async (proposalId: string, invoiceId: string): Promise<void> => {
  await apiFetch<void>(`/proposals/${proposalId}/invoice/${invoiceId}`, { method: "DELETE" });
};

export const uploadProposalInvoice = async (proposalId: string, salesOwnerId: string, file: File): Promise<ProposalInvoice> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("salesOwnerId", salesOwnerId);
  const baseUrl = getApiBaseUrl();
  const token = getAuthToken();
  const response = await fetch(`${baseUrl}/proposals/${proposalId}/invoice`, {
    method: "POST",
    body: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json")
      ? await response.json().catch(() => null)
      : await response.text().catch(() => null);
    if (response.status === 413) {
      throw new Error("Maximum file size is 10MB.");
    }
    if (typeof payload === "string" && payload.trim()) {
      throw new Error(payload);
    }
    throw new Error(payload?.message ?? "Upload failed");
  }
  return response.json();
};

export type DownloadedFile = {
  blob: Blob;
  fileName: string | null;
};

const parseFileNameFromContentDisposition = (value: string | null): string | null => {
  if (!value) return null;
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const quotedMatch = value.match(/filename=\"([^\"]+)\"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];
  const plainMatch = value.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
};

const fetchInvoiceFile = async (proposalId: string, mode: "download" | "view", invoiceId?: string): Promise<DownloadedFile> => {
  const baseUrl = getApiBaseUrl();
  const token = getAuthToken();
  const path = invoiceId
    ? `/proposals/${proposalId}/invoice/${invoiceId}/${mode}`
    : `/proposals/${proposalId}/invoice/${mode}`;
  const response = await fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json")
      ? await response.json().catch(() => null)
      : await response.text().catch(() => null);
    if (typeof payload === "string" && payload.trim()) {
      throw new Error(payload);
    }
    throw new Error(payload?.message ?? "Download failed");
  }
  const blob = await response.blob();
  const fileName = parseFileNameFromContentDisposition(response.headers.get("content-disposition"));
  return { blob, fileName };
};

export const downloadProposalInvoiceFile = async (proposalId: string, invoiceId?: string): Promise<DownloadedFile> => {
  return fetchInvoiceFile(proposalId, "download", invoiceId);
};

export const viewProposalInvoiceFile = async (proposalId: string, invoiceId?: string): Promise<DownloadedFile> => {
  return fetchInvoiceFile(proposalId, "view", invoiceId);
};

export const getProposalReviewLogs = async (id: string): Promise<ProposalReviewLog[]> => {
  return apiFetch<ProposalReviewLog[]>(`/proposals/${id}/review-logs`);
};

export const getProposalRevisions = async (id: string): Promise<ProposalRevisionListResponse> => {
  const payload = await apiFetch<ProposalRevisionListResponse | null>(`/proposals/${id}/revisions`);
  return payload ?? { items: [], total: 0 };
};

export const getProposalRevision = async (id: string, revisionId: string): Promise<ProposalRevision> => {
  return apiFetch<ProposalRevision>(`/proposals/${id}/revisions/${revisionId}`);
};

export const getProposalRevisionPublicPreview = async (id: string, revisionId: string): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/${id}/revisions/${revisionId}/public-preview`);
};

export const restoreProposalRevision = async (id: string, revisionId: string): Promise<Proposal> => {
  return apiFetch<Proposal>(`/proposals/${id}/revisions/${revisionId}/restore`, {
    method: "POST",
  });
};

export type ProposalCommentVersion = {
  content: string;
  edited_at: string;
};

export type ProposalComment = {
  id: string;
  proposal_id: string;
  author?: ProposalRevisionActor | null;
  content: string;
  edited: boolean;
  versions: ProposalCommentVersion[];
  created_at: string;
  updated_at?: string | null;
  edited_at?: string | null;
};

export type ProposalCommentListResponse = {
  items: ProposalComment[];
  total: number;
};

export const listProposalComments = async (id: string): Promise<ProposalCommentListResponse> => {
  const payload = await apiFetch<ProposalCommentListResponse | null>(`/proposals/${id}/comments`);
  return payload ?? { items: [], total: 0 };
};

export const createProposalComment = async (id: string, content: string): Promise<ProposalComment> => {
  return apiFetch<ProposalComment>(`/proposals/${id}/comments`, {
    method: "POST",
    body: JSON.stringify({ content }),
  });
};

export const updateProposalComment = async (
  id: string,
  commentId: string,
  content: string
): Promise<ProposalComment> => {
  return apiFetch<ProposalComment>(`/proposals/${id}/comments/${commentId}`, {
    method: "PATCH",
    body: JSON.stringify({ content }),
  });
};

export const deleteProposalComment = async (id: string, commentId: string): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}/comments/${commentId}`, { method: "DELETE" });
};

export const rejectProposalReview = async (id: string, reason: string): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}/review/reject`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
};

export const resubmitProposalReview = async (id: string, reason: string): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}/review/resubmit`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
};

export const approveProposalReview = async (id: string, operationsOwnerId: string): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}/review/approve`, {
    method: "POST",
    body: JSON.stringify({ operations_owner_id: operationsOwnerId }),
  });
};

export const requestProposalCancellation = async (
  id: string,
  payload: { reason: string; manager_id: string }
): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}/cancellation/request`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const revokeProposalCancellation = async (id: string): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}/cancellation/revoke`, {
    method: "POST",
  });
};

export const approveProposalCancellation = async (
  id: string,
  payload: { decision_type: "controllable" | "uncontrollable" }
): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}/cancellation/approve`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const rejectProposalCancellation = async (id: string): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}/cancellation/reject`, {
    method: "POST",
  });
};

export const reactivateCanceledProposal = async (id: string): Promise<void> => {
  await apiFetch<void>(`/proposals/${id}/cancellation/reactivate`, {
    method: "POST",
  });
};
